import { useState } from 'react'

import cx from 'classnames'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

import { useLoginConfirmMutation, useLoginMutation } from '@/api'
import ArcherImage from '@/assets/images/characters/archer-queen-autumn.png'
import { ExitButton } from '@/components/buttons/ExitButton'
import { StripedContainer } from '@/components/StripedContainer'
import { useLocale } from '@/features/locale/useLocale'
import { closeOverlay } from '@/features/overlay/overlaySlice'
import { LOGIN_STATUS, setEmail, setLoginStatus } from '@/features/user/userSlice'
import { useAppDispatch } from '@/utils/useAppDispatch'
import { useAppSelector } from '@/utils/useAppSelector'

import { LoginSteps } from './LoginSteps'

import SCIDLogo from 'assets/images/logos/supercell_id.svg'

import styles from './Login.module.scss'

interface LoginProps {
  onSuccess?: () => void
  inModal?: boolean
  loading?: boolean
}

const SCID: React.FC<{ close: () => void }> = ({ close }) => {
  const t = useTranslations()

  return (
    <StripedContainer containerClass={cx(styles.Container, styles.SCID)}>
      <ExitButton className={styles.ExitButton} onClick={close} />
      <div>
        <h1>{t('scid_modal_title')}</h1>
        <p>{t('scid_modal_desc')}</p>
        <h1>{t('scid_modal_how')}</h1>
        <p>{t('scid_modal_instruction_1')}</p>
        <p>{t('scid_modal_instruction_2')}</p>
        <p>{t('scid_modal_instruction_3')}</p>
        <p>{t('scid_modal_instruction_4')}</p>
        <p>{t('scid_modal_instruction_5')}</p>
      </div>
      <div className={styles.SCIDArcher}>
        <Image src={ArcherImage} alt="background-left" height={375} width={255} />
      </div>
    </StripedContainer>
  )
}

export const Login: React.FC<LoginProps> = ({ onSuccess, inModal, loading }) => {
  const dispatch = useAppDispatch()
  const loginStatus = useAppSelector((state) => state.user.loginStatus)
  const email = useAppSelector((state) => state.user.email)
  const t = useTranslations()
  const [login, { isLoading: loginLoading }] = useLoginMutation()
  const [loginConfirm, { isLoading: loginConfirmLoading }] = useLoginConfirmMutation()
  const error = useAppSelector((state) => state.user.error)
  const locale = useLocale()
  const [viewSCID, setViewSCID] = useState(false)

  const onEmailSubmit = async (email: string | null) => {
    if (!email) {
      return
    }
    const trimmedEmail = email.trim()
    dispatch(setEmail(trimmedEmail))
    login({ email: trimmedEmail, lang: locale })
  }

  const onPinCancel = () => {
    dispatch(setLoginStatus(LOGIN_STATUS.LOGGED_OUT))
  }

  const onPinSubmit = async (pin: string) => {
    if (email && pin.length === 6) {
      try {
        await loginConfirm({ email, pin }).unwrap()
        onSuccess?.()
      } catch (err) {}
    }
  }

  const isLoading = loginLoading || loginConfirmLoading || !!loading

  const headlineText = loginStatus === LOGIN_STATUS.LOGGED_OUT ? t('login_title') : t('pin_title')
  const bodyText =
    loginStatus === LOGIN_STATUS.LOGGED_OUT
      ? t('login_modal_desc')
      : t.rich('pin_desc', {
          Bold: () => <span className={styles.EmailInDescription}>{email}</span>,
        })

  const BottomText: React.FC = () => {
    return (
      <div className={styles.BottomTextContainer}>
        {loginStatus === LOGIN_STATUS.LOGGED_OUT ? (
          <div className={styles.LoginNewToSupercellID}>
            <p>{t('login_new')}</p>
            <p>
              {t.rich('login_register', {
                TextButton: (children) => (
                  <a href="#" onClick={() => setViewSCID(true)}>
                    {children}
                  </a>
                ),
              })}
            </p>
          </div>
        ) : (
          <p className={styles.PinResend}>
            {t.rich('pin_resend', {
              TextButton: (children) => (
                <a href="#" onClick={() => onEmailSubmit(email)}>
                  {children}
                </a>
              ),
            })}
          </p>
        )}
      </div>
    )
  }

  if (viewSCID) {
    return <SCID close={() => setViewSCID(false)} />
  }

  if (inModal) {
    return (
      <StripedContainer containerClass={styles.Container} stripClass={!inModal ? styles.Hidden : undefined}>
        <ExitButton
          className={styles.ExitButton}
          onClick={() => dispatch(closeOverlay)}
          data-cy="login-modal-close-btn"
        />
        <div className={styles.Logo}>
          <Image alt="logo" src={SCIDLogo} height={46} width={212} />
        </div>
        <h1 className={styles.Headline}>{headlineText}</h1>
        <p className={styles.BodyText}>{bodyText}</p>
        <LoginSteps
          onEmailSubmit={onEmailSubmit}
          onPinCancel={onPinCancel}
          onPinSubmit={onPinSubmit}
          loginStatus={loginStatus}
          error={error}
          loading={isLoading}
        />
        <BottomText />
      </StripedContainer>
    )
  }

  return (
    <>
      <div className={styles.Logo}>
        <Image alt="logo" className={styles.Logo} src={SCIDLogo} objectFit="contain" layout="fill" />
      </div>
      <h1 className={styles.Headline}>{headlineText}</h1>
      <p className={styles.BodyText}>{bodyText}</p>
      <LoginSteps
        onEmailSubmit={onEmailSubmit}
        onPinCancel={onPinCancel}
        onPinSubmit={onPinSubmit}
        loginStatus={loginStatus}
        error={error}
        loading={isLoading}
      />
      <BottomText />
    </>
  )
}
